import { Component } from '@theme/component';

/**
 * @typedef {Object} CollectionPlpFiltersRefs
 * @property {HTMLButtonElement} trigger - Filter bar trigger
 * @property {HTMLElement} [panel] - Dropdown panel
 */

/**
 * Desktop PLP filter dropdown below the green pill bar.
 * Mutually exclusive with the sort dropdown in the same bar.
 *
 * @extends {Component<CollectionPlpFiltersRefs>}
 */
class CollectionPlpFilters extends Component {
  requiredRefs = ['trigger'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.addEventListener('keydown', this.#handleKeydown);
    // Capture: `toggle` on <details> does not always bubble.
    this.addEventListener('toggle', this.#handleSortingToggle, true);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.removeEventListener('keydown', this.#handleKeydown);
    this.removeEventListener('toggle', this.#handleSortingToggle, true);
  }

  /**
   * @param {Event} event
   */
  #handleSortingToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open) {
      return;
    }

    if (!details.closest('sorting-filter-component')) {
      return;
    }

    this.closePanel();
  };

  #closeSorting() {
    for (const details of this.querySelectorAll('sorting-filter-component details[open]')) {
      details.removeAttribute('open');

      const summary = details.querySelector('summary');
      if (summary instanceof HTMLElement) {
        summary.setAttribute('aria-expanded', 'false');
      }
    }
  }

  /**
   * @param {PointerEvent} event
   */
  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Node)) {
      return;
    }

    if (!this.contains(event.target)) {
      this.closePanel();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    this.closePanel();

    if (this.refs.trigger) {
      this.refs.trigger.focus();
    }
  };

  togglePanel = () => {
    const panel = this.refs.panel;
    if (!panel || !this.refs.trigger) {
      return;
    }

    const isOpen = this.refs.trigger.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.closePanel();
      return;
    }

    this.openPanel();
  };

  openPanel = () => {
    const panel = this.refs.panel;
    if (!panel || !this.refs.trigger) {
      return;
    }

    this.#closeSorting();
    panel.hidden = false;
    this.refs.trigger.setAttribute('aria-expanded', 'true');
  };

  closePanel = () => {
    const panel = this.refs.panel;
    if (panel) {
      panel.hidden = true;
    }

    if (this.refs.trigger) {
      this.refs.trigger.setAttribute('aria-expanded', 'false');
    }
  };

  applyFilters = () => {
    const panel = this.refs.panel;

    if (panel) {
      for (const priceFacet of panel.querySelectorAll('price-facet-component')) {
        if ('preparePriceInputs' in priceFacet && typeof priceFacet.preparePriceInputs === 'function') {
          priceFacet.preparePriceInputs();
        }
      }
    }

    const facetsForm = this.closest('facets-form-component');

    if (facetsForm && 'updateFilters' in facetsForm && typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }

    this.closePanel();
  };
}

if (!customElements.get('collection-plp-filters')) {
  customElements.define('collection-plp-filters', CollectionPlpFilters);
}
